/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited 2023.
 */

// eslint-disable-next-line no-restricted-imports
import NextLink, { LinkProps } from 'next/link';
import { Link, Button, SxProps, Theme, IconButton, ButtonBase, Chip } from '@mui/material';
import React, { ComponentProps, FC, forwardRef } from 'react';
import { Switch } from '@/utils/switch';
import { ImageProps } from 'next/image';
import { constructNextUrl, useNextRouter } from '@/data/Content/_NextRouter';
import { MaterialImage } from '@/components/blocks/MaterialImage';
import { useSettings } from '@/data/Settings';
import { UrlObject } from 'url';
import { combineSX } from '@/utils/combineSX';
import { LinkableLinkSX } from '@/components/blocks/Linkable/style/link';
import getValueByPathFromUnknown from 'integration/utils/getValueByPathFromUnknown';
import valueIsRecord from 'integration/utils/typeAssertions/valueIsRecord';

type LinkablePropsCommon = {
	href?: LinkProps['href'];
	back?: boolean;
	sx?: SxProps<Theme>;
	[key: string]: unknown;
};

type LinkablePropsText = {
	type?: 'link' | 'button' | 'inline' | 'button-icon' | 'button-base' | 'chip';
	children?: JSX.Element | JSX.Element[] | string | React.ReactNode;
} & LinkablePropsCommon;

type LinkablePropsImage = {
	type: 'image';
} & LinkablePropsCommon &
	ImageProps;

type LinkableProps = LinkablePropsText | LinkablePropsImage;

/**
 * Used to make any component linkable.
 * `legacyBehavior` will potentially be removed in future release. Try to use with
 * `legacyBehavior={false}`
 */
export const LinkWrap: FC<
	Omit<ComponentProps<typeof NextLink>, 'href'> & { href?: string | UrlObject; back?: boolean }
> = ({ href, children, back, onClick, ...props }) => {
	const router = useNextRouter();
	const {
		settings: { storeToken },
	} = useSettings();

	const _href = constructNextUrl(router.asPath, href, storeToken);

	const handleOnClick: React.MouseEventHandler<HTMLButtonElement> = (event) => {
		if (back) {
			event.preventDefault();
			router.back();
		}
	};

	return _href ? (
		<NextLink href={_href} passHref legacyBehavior {...props}>
			{children}
		</NextLink>
	) : back ? (
		<ButtonBase onClick={handleOnClick}>{children}</ButtonBase>
	) : (
		<>{children}</>
	);
};

const persistInLocalStorage = (values: Record<string, unknown>) => {
	if (typeof window !== 'undefined') {
		Object.entries(values).forEach(([key, value]) => {
			localStorage.setItem(key, JSON.stringify(value));
		});
	}
};

/**
 * Used for general linking, where children can be a simple string label.
 */
export const Linkable: FC<LinkableProps> = forwardRef<HTMLAnchorElement, any>(
	(
		{
			type = 'link',
			variant,
			href,
			children,
			sx = {},
			alt = '',
			back,
			buttonType,
			onClick,
			...props
		},
		ref
	) => {
		const handleOnClick = (event: any) => {
			const lsInHref = getValueByPathFromUnknown(href, ['localstorage']);
			if (valueIsRecord(lsInHref)) {
				persistInLocalStorage(lsInHref);
			}
			onClick?.(event);
		};

		return Switch(type)
			.case('link', () => {
				const _sx = variant
					? combineSX([LinkableLinkSX(variant), ...(Array.isArray(sx) ? sx : [sx])])
					: sx;

				if (href === '#') {
					return (
						<Link ref={ref} {...props} sx={_sx} onClick={handleOnClick}>
							{children}
						</Link>
					);
				}

				if (typeof href === 'string' && (href.startsWith('mailto:') || href.startsWith('tel:'))) {
					return (
						<Link ref={ref} href={href} {...props} sx={_sx} onClick={handleOnClick}>
							{children}
						</Link>
					);
				}

				return (
					<LinkWrap href={href} back={back}>
						{href ? (
							<Link ref={ref} {...props} sx={_sx} onClick={handleOnClick}>
								{children}
							</Link>
						) : (
							children
						)}
					</LinkWrap>
				);
			})
			.case('button', () => (
				<LinkWrap href={href}>
					<Button
						ref={ref}
						component="a"
						variant={variant}
						type={buttonType}
						sx={sx}
						{...props}
						onClick={handleOnClick}
					>
						{children}
					</Button>
				</LinkWrap>
			))
			.case('inline', () => (
				<LinkWrap href={href}>
					<Button
						ref={ref}
						component="a"
						{...props}
						sx={sx}
						variant="inline"
						onClick={handleOnClick}
					>
						{children}
					</Button>
				</LinkWrap>
			))
			.case('image', () => (
				<LinkWrap href={href}>
					<Link sx={sx} ref={ref} onClick={handleOnClick}>
						<MaterialImage alt={alt} {...props} />
					</Link>
				</LinkWrap>
			))
			.case('button-icon', () => (
				<LinkWrap href={href}>
					<IconButton ref={ref} {...props} sx={sx} onClick={handleOnClick}>
						{children}
					</IconButton>
				</LinkWrap>
			))
			.case('external', () => (
				<Link ref={ref} href={href} {...props} sx={sx} onClick={handleOnClick}>
					{children}
				</Link>
			))
			.case('button-base', () => (
				<LinkWrap href={href}>
					<ButtonBase ref={ref} sx={sx} {...props} onClick={handleOnClick}>
						{children}
					</ButtonBase>
				</LinkWrap>
			))
			.case('chip', () => (
				<LinkWrap href={href}>
					<Chip ref={ref} sx={sx} {...props} onClick={handleOnClick}>
						{children}
					</Chip>
				</LinkWrap>
			))
			.defaultTo(() => null);
	}
);
